import { useState } from 'react';
import { getColumns } from './Columns';
import { useEstabsContext } from '../Context';
import CustomDataTable from '../../../../components/table/Table';
import { EstabelecimentosService } from '../Service';
import { useAppContext } from '../../../../contexts/AppContext';
import { Modal } from '../../../../components/Modal';
import {
    SALONFLOW_PLAN_LABEL,
    normalizePlanKey,
} from '../../../../plans/salonflowPlans';
import type { SalonFlowPlanKey } from '../../../../plans/salonflowPlans';

const service = new EstabelecimentosService();

export default function EstabelecimentosList() {
    const { data, loading, reloadData } = useEstabsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
    const [deletingActionId, setDeletingActionId] = useState<string | null>(null);
    const [deleteRow, setDeleteRow] = useState<any | null>(null);
    const [planRow, setPlanRow] = useState<any | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<SalonFlowPlanKey>('trial');

    const systemPlanOptions: SalonFlowPlanKey[] = [
        'trial',
        'basic',
        'professional',
        'premium',
    ];

    const onActivateBilling = async (row: any) => {
        try {
            setLoadingActionId(row?._id);
            await service.activateBilling(row?._id);
            await reloadData();
            successSnackbar('Pagamento ativado com sucesso.');
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Nao foi possivel ativar o pagamento.');
        } finally {
            setLoadingActionId(null);
        }
    };

    const onDeactivateBilling = async (row: any) => {
        try {
            setLoadingActionId(row?._id);
            await service.deactivateBilling(row?._id);
            await reloadData();
            successSnackbar('Pagamento desativado. O estabelecimento foi bloqueado.');
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Nao foi possivel desativar o pagamento.');
        } finally {
            setLoadingActionId(null);
        }
    };

    const onDelete = (row: any) => {
        setDeleteRow(row);
    };

    const onChangeSystemPlan = (row: any) => {
        setPlanRow(row);
        setSelectedPlan(normalizePlanKey(row?.systemPlanKey));
    };

    const confirmChangeSystemPlan = async () => {
        if (!planRow?._id) return;
        try {
            setLoadingActionId(planRow._id);
            await service.updateSystemPlan(planRow._id, selectedPlan);
            await reloadData();
            successSnackbar('Plano comercial atualizado com sucesso.');
            setPlanRow(null);
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Nao foi possivel alterar o plano comercial.');
        } finally {
            setLoadingActionId(null);
        }
    };

    const confirmDelete = async () => {
        if (!deleteRow?._id) return;
        try {
            setDeletingActionId(deleteRow._id);
            await service.delete(deleteRow._id);
            await reloadData();
            successSnackbar('Estabelecimento removido com sucesso.');
            setDeleteRow(null);
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Nao foi possivel remover o estabelecimento.');
        } finally {
            setDeletingActionId(null);
        }
    };

    return (
        <>
            <CustomDataTable
                columns={getColumns({
                    onActivateBilling,
                    onDeactivateBilling,
                    onChangeSystemPlan,
                    onDelete,
                    loadingActionId,
                    deletingActionId
                })}
                data={data}
                loading={loading}
                pagination
                highlightOnHover
                striped
            />

            {!!deleteRow && (
                <Modal
                    title="Confirmar remocao"
                    subTitle={`Voce deseja remover o estabelecimento \"${deleteRow?.name || '-'}\"?`}
                    onClose={() => (deletingActionId ? null : setDeleteRow(null))}
                >
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setDeleteRow(null)}
                            disabled={!!deletingActionId}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-error"
                            onClick={confirmDelete}
                            disabled={!!deletingActionId}
                        >
                            {deletingActionId ? 'Removendo...' : 'Remover estabelecimento'}
                        </button>
                    </div>
                </Modal>
            )}

            {!!planRow && (
                <Modal
                    title="Alterar plano comercial"
                    subTitle={`Selecione o plano de uso do estabelecimento "${planRow?.name || '-'}".`}
                    onClose={() => (loadingActionId ? null : setPlanRow(null))}
                >
                    <div className="space-y-5">
                        <label className="form-control w-full">
                            <div className="label">
                                <span className="label-text font-semibold">Plano atual</span>
                            </div>
                            <select
                                className="select select-bordered w-full"
                                value={selectedPlan}
                                disabled={!!loadingActionId}
                                onChange={(event) =>
                                    setSelectedPlan(event.target.value as SalonFlowPlanKey)
                                }
                            >
                                {systemPlanOptions.map((planKey) => (
                                    <option key={planKey} value={planKey}>
                                        {SALONFLOW_PLAN_LABEL[planKey]}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="rounded-xl border border-warning/25 bg-warning/10 p-3 text-sm text-base-content/75">
                            Alterar este campo muda apenas o plano comercial do SaaS. Ativar ou desativar cobranca
                            continua sendo uma acao separada.
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setPlanRow(null)}
                                disabled={!!loadingActionId}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={confirmChangeSystemPlan}
                                disabled={!!loadingActionId}
                            >
                                {loadingActionId ? 'Salvando...' : 'Salvar plano'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}
